import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { AddressAutocomplete } from "../forms/widgets/AddressAutocomplete";
import { laenderMitBestandswert } from "../../data/laender";
import { plzPruefen } from "../forms/validation/laenderprofil";
import { ergaenzePlzOrt } from "../forms/google/plzOrtErgaenzen";

/**
 * Straße / PLZ / Ort / Land — der eine Adressbaustein der Immobilien-Erfassung.
 *
 * Zwei querschnittliche Entscheidungen stecken hier drin (Plan §0):
 *  - **E9:** die Straße läuft über das vorhandene Google-Places-Widget
 *    `AddressAutocomplete`. Ohne gesetzten Maps-Key bleibt `places` null und
 *    das Feld verhält sich exakt wie ein normales Texteingabefeld — das war
 *    schon vorher so und darf nicht verloren gehen.
 *  - **E10:** Land ist eine Auswahl über der gemeinsamen Länderliste
 *    (`data/laender.ts`). Bestandswerte sind Freitext; ein gespeicherter Wert,
 *    der nicht in der Liste steht, wird über `laenderMitBestandswert()`
 *    mit angeboten statt beim Öffnen still verworfen.
 *
 * Seit 10/2026 steht das Land **vor** der Straße (wie in den Kontaktmasken):
 * Die Straßensuche sucht im gewählten Land, die PLZ wird gegen das Land
 * geprüft, und fehlt nach der Auswahl die PLZ oder der Ort, schlägt
 * `ergaenzePlzOrt` die andere Hälfte nach („PLZ wird nicht completed").
 *
 * Die Keys sind Parameter, damit derselbe Baustein die Risikoanschrift
 * (`street`/`postalCode`/`city`/`country`) und die Adresse eines Beteiligten
 * (`eigentuemerStrasse`/…) bedienen kann.
 */

const useStyles = makeStyles({
  grid: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: "12px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  // Wichtig: dieselben Werte wie das Label/Input der Maske — die Klassen werden
  // über mergeClasses in AddressAutocomplete hineingereicht.
  label: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 400,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":hover": { ...shorthands.borderColor("#d4cfd9") },
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  autocompleteField: { rowGap: "4px" },
  meldung: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: "#a4262c",
  },
});

export type AdressFeldKeys = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

export type AdressWerte = {
  street: string;
  postalCode: string;
  city: string;
  country: string;
};

type Props = {
  /** Answer-Keys, unter denen die vier Felder liegen. */
  keys: AdressFeldKeys;
  values: AdressWerte;
  onChange: (key: string, value: string) => void;
  /** Eindeutiges Präfix für `id`/`htmlFor` — mehrere Adressen pro Seite. */
  idPrefix: string;
  strasseLabel?: string;
  /** Land ausblenden, wo die Spec keins vorsieht. Default: anzeigen. */
  withCountry?: boolean;
};

const AdressFelder = ({
  keys,
  values,
  onChange,
  idPrefix,
  strasseLabel = "Straße & Hausnummer",
  withCountry = true,
}: Props) => {
  const styles = useStyles();
  const plzUrteil = plzPruefen(values.postalCode, values.country);

  // Fehlende Hälfte von PLZ/Ort nachschlagen. Geschrieben wird nur in ein
  // Feld, das zu diesem Zeitpunkt noch leer ist — eine Eingabe des Nutzers
  // überschreibt die Ergänzung nie.
  const ergaenze = (stand: AdressWerte) => {
    void ergaenzePlzOrt(stand).then((e) => {
      if (e.postalCode && !stand.postalCode.trim()) onChange(keys.postalCode, e.postalCode);
      if (e.city && !stand.city.trim()) onChange(keys.city, e.city);
    });
  };

  return (
    <div className={styles.grid}>
      {withCountry && (
        <div className={styles.field}>
          <span className={styles.label}>Land</span>
          <select
            className={styles.input}
            name={`${idPrefix}-country`}
            value={values.country}
            onChange={(e) => onChange(keys.country, e.target.value)}
          >
            <option value="">— wählen —</option>
            {laenderMitBestandswert(values.country).map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </div>
      )}
      <AddressAutocomplete
        label={strasseLabel}
        name={`${idPrefix}-street`}
        value={values.street}
        land={values.country}
        onChange={(v) => onChange(keys.street, v)}
        onResolved={(a) => {
          onChange(keys.street, a.street);
          if (a.postalcode) onChange(keys.postalCode, a.postalcode);
          if (a.city) onChange(keys.city, a.city);
          if (!a.postalcode || !a.city) {
            ergaenze({
              ...values,
              street: a.street,
              postalCode: a.postalcode || values.postalCode,
              city: a.city || values.city,
            });
          }
        }}
        fieldClassName={styles.autocompleteField}
        labelClassName={styles.label}
        inputClassName={styles.input}
      />
      <div className={styles.twoCol}>
        <div className={styles.field}>
          <span className={styles.label}>PLZ</span>
          <input
            className={styles.input}
            name={`${idPrefix}-postalCode`}
            value={values.postalCode}
            inputMode={plzUrteil.nurZiffern ? "numeric" : undefined}
            placeholder={plzUrteil.nurZiffern ? undefined : plzUrteil.beispiel || undefined}
            aria-invalid={!plzUrteil.ok || undefined}
            onChange={(e) => onChange(keys.postalCode, e.target.value)}
            onBlur={() => ergaenze(values)}
          />
          {!plzUrteil.ok && plzUrteil.meldung && (
            <span className={styles.meldung}>{plzUrteil.meldung}</span>
          )}
        </div>
        <div className={styles.field}>
          <span className={styles.label}>Ort</span>
          <input
            className={styles.input}
            name={`${idPrefix}-city`}
            value={values.city}
            onChange={(e) => onChange(keys.city, e.target.value)}
            onBlur={() => ergaenze(values)}
          />
        </div>
      </div>
    </div>
  );
};

export default AdressFelder;
